import React, { useState, useEffect, useCallback } from 'react';
import { getTransactions } from '../services/transactionService';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import ExcelColumnFilter from '../components/ExcelColumnFilter';

const Transactions = () => {
    const [transactions, setTransactions] = useState([]);
    const [error, setError] = useState('');
    const [columnFilters, setColumnFilters] = useState({});

    const fetchTransactions = useCallback(async () => {
        try {
            const data = await getTransactions();
            setTransactions(data);
        } catch (err) {
            setError('No se pudieron obtener las transacciones.');
            console.error(err);
        }
    }, []);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    const updateColumnFilter = (column, values) => {
        setColumnFilters((prev) => ({
            ...prev,
            [column]: values
        }));
    };

    const clearFilters = () => setColumnFilters({});

    const getColumnValue = (transaction, column) => {
        if (column === 'id') {
            return (transaction.id ?? '').toString();
        }
        if (column === 'acquirer_transaction_id') {
            return transaction.acquirer_transaction_id || '';
        }
        if (column === 'merchant_name') {
            return transaction.merchant_name || '';
        }
        if (column === 'amount') {
            return (transaction.amount ?? '').toString();
        }
        if (column === 'transaction_type') {
            return transaction.transaction_type || '';
        }
        if (column === 'transaction_status') {
            return transaction.transaction_status || '';
        }
        return '';
    };

    const matchesColumnFilter = (value, selectedOptions) => {
        if (selectedOptions === undefined) {
            return true;
        }
        return selectedOptions.includes(value);
    };

    const filteredTransactions = (transactions || []).filter((transaction) => (
        matchesColumnFilter(getColumnValue(transaction, 'id'), columnFilters.id) &&
        matchesColumnFilter(getColumnValue(transaction, 'acquirer_transaction_id'), columnFilters.acquirer_transaction_id) &&
        matchesColumnFilter(getColumnValue(transaction, 'merchant_name'), columnFilters.merchant_name) &&
        matchesColumnFilter(getColumnValue(transaction, 'amount'), columnFilters.amount) &&
        matchesColumnFilter(getColumnValue(transaction, 'transaction_type'), columnFilters.transaction_type) &&
        matchesColumnFilter(getColumnValue(transaction, 'transaction_status'), columnFilters.transaction_status)
    ));

    return (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" component="h1">
                    Transacciones
                </Typography>
                <Button variant="text" onClick={clearFilters}>Limpiar filtros</Button>
            </Box>
            {error && <Typography color="error">{error}</Typography>}
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="ID"
                                    options={transactions.map((item) => getColumnValue(item, 'id'))}
                                    selectedOptions={columnFilters.id}
                                    onChange={(values) => updateColumnFilter('id', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="ID transacción adquirente"
                                    options={transactions.map((item) => getColumnValue(item, 'acquirer_transaction_id'))}
                                    selectedOptions={columnFilters.acquirer_transaction_id}
                                    onChange={(values) => updateColumnFilter('acquirer_transaction_id', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Comercio"
                                    options={transactions.map((item) => getColumnValue(item, 'merchant_name'))}
                                    selectedOptions={columnFilters.merchant_name}
                                    onChange={(values) => updateColumnFilter('merchant_name', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Monto"
                                    options={transactions.map((item) => getColumnValue(item, 'amount'))}
                                    selectedOptions={columnFilters.amount}
                                    onChange={(values) => updateColumnFilter('amount', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Tipo"
                                    options={transactions.map((item) => getColumnValue(item, 'transaction_type'))}
                                    selectedOptions={columnFilters.transaction_type}
                                    onChange={(values) => updateColumnFilter('transaction_type', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Estado"
                                    options={transactions.map((item) => getColumnValue(item, 'transaction_status'))}
                                    selectedOptions={columnFilters.transaction_status}
                                    onChange={(values) => updateColumnFilter('transaction_status', values)}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTransactions.map((transaction) => (
                            <TableRow key={transaction.id}>
                                <TableCell>{transaction.id}</TableCell>
                                <TableCell>{transaction.acquirer_transaction_id}</TableCell>
                                <TableCell>{transaction.merchant_name}</TableCell>
                                <TableCell>{transaction.amount}</TableCell>
                                <TableCell>{transaction.transaction_type}</TableCell>
                                <TableCell>{transaction.transaction_status}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default Transactions;
